import { Text, Tooltip, useDesignTokens } from "@gradience/ui";
import React from "react";

interface BoxPlotBarProps {
  /** minimum of your domain (e.g. 0) */
  domainMin: number;
  /** maximum of your domain (e.g. 40 or 100) */
  domainMax: number;
  /** whisker low (often the min) */
  whiskerMin: number;
  /** 25th percentile */
  q1: number;
  /** median (50th percentile) */
  median: number;
  /** 75th percentile */
  q3: number;
  /** whisker high (often the max) */
  whiskerMax: number;
  /** full width of the bar container (any valid CSS width) */
  width?: string;
  /** height of the bar (px) */
  height?: number;
}

const BoxPlotBar: React.FC<BoxPlotBarProps> = ({
  domainMin,
  domainMax,
  whiskerMin,
  q1,
  median,
  q3,
  whiskerMax,
  width = "100%",
  height = 32,
}) => {
  const designTokens = useDesignTokens();
  const color = designTokens.colors.misc.Brand;
  const normalize = (value: number) =>
    ((value - domainMin) / (domainMax - domainMin)) * 100;

  const boxLeft = normalize(q1);
  const boxWidth = normalize(q3) - boxLeft;
  const medianPos = normalize(median);
  const whiskerLeft = normalize(whiskerMin);
  const whiskerWidth = normalize(whiskerMax) - whiskerLeft;

  return (
    <div style={{ position: "relative", width, height: `${height}px` }}>
      {/* horizontal whisker line */}
      <div
        style={{
          position: "absolute",
          left: `${whiskerLeft}%`,
          width: `${whiskerWidth}%`,
          top: `${height / 2 - 1}px`,
          height: 2,
          backgroundColor: color,
        }}
      />

      {/* left whisker cap */}
      <div
        style={{
          position: "absolute",
          left: `${whiskerLeft}%`,
          width: 2,
          top: 0,
          height: `${height}px`,
          backgroundColor: color,
        }}
      />
      {/* right whisker cap */}
      <div
        style={{
          position: "absolute",
          left: `${normalize(whiskerMax)}%`,
          width: 2,
          top: 0,
          height: `${height}px`,
          backgroundColor: color,
        }}
      />

      {/* IQR box (25th–75th) */}
      <div
        style={{
          position: "absolute",
          left: `${boxLeft}%`,
          width: `${boxWidth}%`,
          height: `${height}px`,
          backgroundColor: color,
          borderRadius: 12,
        }}
      />

      {/* median dot */}
      <Tooltip
        content={
          <Text textStyle="14px - Medium">Median: {median.toFixed(2)}</Text>
        }
      >
        <div
          style={{
            position: "absolute",
            left: `${medianPos}%`,
            width: 10,
            height: 10,
            top: `${height / 2 - 5}px`,
            backgroundColor: designTokens.colors.misc["08"],
            borderRadius: "50%",
          }}
        />
      </Tooltip>
    </div>
  );
};

export default BoxPlotBar;
